import { Game, Player, Grid, Tile } from "./models";
import io from 'socket.io-client'

export class Client {

    apiUrl: string;
    socketUrl: string;

    constructor(url: string, socketUrl: string) {
        this.apiUrl = url
        this.socketUrl = socketUrl
    }

    async newGame(): Promise<Game> {
        let res = await fetch(`${this.apiUrl}/games`, { method: "POST" })
        await checkResponse(res)
        let out:{game: Game} = await res.json()
        return out.game
    }

    async getGame(gameId: string): Promise<Game> {
        let res = await fetch(`${this.apiUrl}/games/${gameId}`)
        await checkResponse(res)
        let out:{game: Game} = await res.json()
        return out.game
    }

    async addPlayer(gameId: string, playerName: string, isBot: boolean = false): Promise<Player> {
        const body = JSON.stringify({
            name: playerName,
            isBot: isBot,
        })
        let res = await fetch(`${this.apiUrl}/games/${gameId}/players`, { method: "POST", body: body })
        await checkResponse(res)
        let out:{player: Player} = await res.json()
        return out.player
    }

    async startGame(gameId: string): Promise<Game> {
        let res = await fetch(`${this.apiUrl}/games/${gameId}/start`, { method: "POST" })
        await checkResponse(res)
        let out:{game: Game} = await res.json()
        return out.game
    }

    async placeTiles(gameId: string, placement: Grid): Promise<Game> {
        const body = JSON.stringify({ placement })
        let res = await fetch(`${this.apiUrl}/games/${gameId}/place`, { method: "POST", body: body })
        await checkResponse(res)
        let out:{game: Game} = await res.json()
        return out.game
    }

    async tradeTiles(gameId: string, tiles: Tile[]): Promise<Game> {
        const body = JSON.stringify({ tiles })
        let res = await fetch(`${this.apiUrl}/games/${gameId}/trade`, { method: "POST", body: body })
        await checkResponse(res)
        let out:{game: Game} = await res.json()
        return out.game
    }

    async getGameIdByShareCode(code: string): Promise<string> {
        let res = await fetch(`${this.apiUrl}/shares/${code}/game`)
        await checkResponse(res)
        let out:{gameId: string} = await res.json()
        return out.gameId
    }

    async getShareCodeByGameId(gameId: string): Promise<string> {
        let res = await fetch(`${this.apiUrl}/shares/${gameId}/code`)
        await checkResponse(res)
        let out:{code: string} = await res.json()
        return out.code
    }

    // subscribe can be used to subscribe to changes made to a game. Every time
    // the game is changed, it calls the given callback function with the
    // updated version of the game.
    //
    // Internally it tries to use socket.io to get game update notifications
    // from the server. And it falls back to asking the API for updates every
    // second.
    subscribe(gameId: string, callback: (game: Game) => any): () => void {
        let lastVersion = 0
        const fetchGame = () => {
            this.getGame(gameId).then(game => {
                if (game.version > lastVersion) {
                    lastVersion = game.version
                    callback(game)
                }
            })
        }
        let fallback: NodeJS.Timeout
        const startFallback = () => { fallback = setInterval(fetchGame, 1000) }
        const stopFallback = () => clearInterval(fallback)

        startFallback()
        const socket = io.connect(this.socketUrl+"/games", {
            transports: ["websocket", "polling"],
        })
        socket.on("connect", () => {
            socket.emit("subscribe", gameId, () => {
                stopFallback()
            })

            socket.on("game updated", (updatedId: string) => {
                if (updatedId === gameId) {
                    fetchGame()
                }
            })

            socket.on("disconnected", () => {
                startFallback()
            })
        })
        
        return () => {
            socket.disconnect()
            stopFallback()
        }
    }
    
}

async function checkResponse(res: Response) {
    if (res.status > 199 && res.status < 300) return
    let out:{error: Error} = await res.json()
    throw new Error(out.error.message)
}

const apiURL = process.env.REACT_APP_API_URL || "/api"
const socketURL = process.env.REACT_APP_SOCKET_URL || ""
const client = new Client(apiURL, socketURL)

export default client